import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { requestDetailPath } from "@vv/types";
import { Button, DataTable, FullPageStatus, Input, type DataTableColumn } from "@vv/ui";
import { fetchTeams, useTeamsDispatch, useTeamsSelector } from "@vv/teams-frontend";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PriorityChip } from "../components/PriorityChip";
import { StatusChip } from "../components/StatusChip";
import {
  ALL_PRIORITIES,
  RequestStatus,
  VALIDATION_TYPE_LABELS,
  type ValidationRequest,
} from "../domain/request.types";
import { useRequestsDispatch, useRequestsSelector } from "../state/hooks";
import { fetchRequests } from "../state/requests.thunks";

const ALL_STATUSES: RequestStatus[] = Object.values(RequestStatus);

export function RequestsListPage() {
  const dispatch = useRequestsDispatch();
  const teamsDispatch = useTeamsDispatch();
  const navigate = useNavigate();

  const requests = useRequestsSelector((state) => state.requests.requests);
  const status = useRequestsSelector((state) => state.requests.status);
  const error = useRequestsSelector((state) => state.requests.error);
  const teams = useTeamsSelector((state) => state.teams.teams);

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<string>("");

  useEffect(() => {
    dispatch(fetchRequests());
    teamsDispatch(fetchTeams());
  }, [dispatch, teamsDispatch]);

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    teams.forEach((team) => map.set(team.id, team.name));
    return map;
  }, [teams]);

  const filtered = requests.filter((request) => {
    if (statusFilter && request.status !== statusFilter) return false;
    if (priorityFilter && request.priority !== priorityFilter) return false;
    return true;
  });

  const columns: DataTableColumn<ValidationRequest>[] = [
    { key: "displayId", header: "Ref", render: (r) => r.displayId, sortable: true },
    { key: "title", header: "Title", render: (r) => r.title, sortable: true },
    {
      key: "team",
      header: "Team",
      render: (r) => teamNameById.get(r.teamId) ?? "—",
    },
    {
      key: "validationType",
      header: "Type",
      render: (r) => VALIDATION_TYPE_LABELS[r.validationType],
    },
    { key: "priority", header: "Priority", render: (r) => <PriorityChip priority={r.priority} /> },
    { key: "status", header: "Status", render: (r) => <StatusChip status={r.status} /> },
    {
      key: "createdAt",
      header: "Submitted",
      render: (r) => new Date(r.createdAt).toLocaleDateString(),
      sortable: true,
      sortValue: (r) => r.createdAt,
    },
  ];

  if (status === "loading" && requests.length === 0) {
    return <FullPageStatus variant="loading" title="Loading requests..." />;
  }

  if (status === "failed" && requests.length === 0) {
    return (
      <FullPageStatus
        variant="error"
        title="Could not load requests"
        description={error ?? undefined}
        action={
          <Button variant="contained" onClick={() => dispatch(fetchRequests())}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Validation Requests
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/requests/new")}
        >
          New request
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Input
          select
          label="Status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {ALL_STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Input>
        <Input
          select
          label="Priority"
          value={priorityFilter}
          onChange={(event) => setPriorityFilter(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All priorities</MenuItem>
          {ALL_PRIORITIES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Input>
      </Stack>

      {status === "succeeded" && filtered.length === 0 ? (
        <FullPageStatus
          variant="empty"
          title="No validation requests"
          description="Submit a new request to get started."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/requests/new")}
            >
              New request
            </Button>
          }
        />
      ) : (
        <DataTable<ValidationRequest>
          columns={columns}
          rows={filtered}
          getRowId={(request) => request.id}
          loading={status === "loading"}
          onRowClick={(request) => navigate(requestDetailPath(request.id))}
        />
      )}
    </Box>
  );
}
